import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Eye, Send, Pencil, Trash2, X, Loader2, Copy, Check,
  Mail, FileText, ToggleLeft, ChevronDown, AlertTriangle, Sparkles
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: 'order', label: 'Order', color: 'bg-blue-100 text-blue-700' },
  { value: 'payment', label: 'Payment', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'account', label: 'Account', color: 'bg-purple-100 text-purple-700' },
  { value: 'shipping', label: 'Shipping', color: 'bg-amber-100 text-amber-700' },
  { value: 'verifactu', label: 'VeriFactu', color: 'bg-orange-100 text-orange-700' },
  { value: 'general', label: 'General', color: 'bg-slate-100 text-slate-700' },
];

const EVENT_DESCRIPTIONS = {
  order: 'Order-related notifications',
  payment: 'Payment and invoice notifications',
  account: 'Account and login notifications',
  shipping: 'Shipping and delivery updates',
  verifactu: 'VeriFactu fiscal invoice notifications',
  general: 'General purpose notifications',
};

const CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

export default function EmailTemplatesPage() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewMode, setPreviewMode] = useState('email');
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(null);
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  const [showVars, setShowVars] = useState(false);
  const bodyRef = useRef(null);

  const [form, setForm] = useState({
    name: '', subject: '', body: '', event_type: 'general',
    channel: 'email', is_active: true, variables: [],
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadTemplates(); loadVariables(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/email-templates`, { headers });
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadVariables = async () => {
    try {
      const res = await fetch(`${API}/api/email-templates/variables`, { headers });
      const data = await res.json();
      setVariables(data || {});
    } catch (e) {
      console.error('Failed to load variables', e);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ name: '', subject: '', body: '', event_type: 'general', channel: 'email', is_active: true, variables: [] });
    setShowEditor(true);
  };

  const openEditModal = (tmpl) => {
    setEditingId(tmpl.id);
    setForm({
      name: tmpl.name, subject: tmpl.subject, body: tmpl.body,
      event_type: tmpl.event_type, channel: tmpl.channel,
      is_active: tmpl.is_active, variables: tmpl.variables || [],
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body) {
      toast.error(t('et_fill_required'));
      return;
    }
    setSaving(true);
    try {
      const url = editingId ? `${API}/api/email-templates/${editingId}` : `${API}/api/email-templates`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      toast.success(editingId ? t('et_updated') : t('et_created'));
      setShowEditor(false);
      loadTemplates();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('et_confirm_delete'))) return;
    try {
      const res = await fetch(`${API}/api/email-templates/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success(t('et_deleted'));
      loadTemplates();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handlePreview = async (id) => {
    try {
      const res = await fetch(`${API}/api/email-templates/${id}/preview`, { method: 'POST', headers });
      if (!res.ok) throw new Error('Failed to preview');
      const data = await res.json();
      setPreviewData(data);
      setPreviewMode('email');
      setShowPreview(true);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleSendTest = async (id) => {
    setSendingTest(id);
    try {
      const res = await fetch(`${API}/api/email-templates/${id}/send-test`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send');
      toast.success(data.message || t('et_test_sent'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSendingTest(null);
    }
  };

  const handleSeedDefaults = async () => {
    setSeedingDefaults(true);
    try {
      const res = await fetch(`${API}/api/email-templates/seed-defaults`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to seed');
      toast.success(data.message || `Added ${data.added} templates`);
      loadTemplates();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSeedingDefaults(false);
    }
  };

  const insertVariable = (varName) => {
    const tag = `{{${varName}}}`;
    if (bodyRef.current) {
      const textarea = bodyRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = form.body.substring(0, start) + tag + form.body.substring(end);
      setForm(prev => ({
        ...prev,
        body: newBody,
        variables: prev.variables.includes(varName) ? prev.variables : [...prev.variables, varName],
      }));
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    } else {
      setForm(prev => ({
        ...prev,
        body: prev.body + tag,
        variables: prev.variables.includes(varName) ? prev.variables : [...prev.variables, varName],
      }));
    }
    setShowVars(false);
  };

  const getEventColor = (type) => {
    const ev = EVENT_TYPES.find(e => e.value === type);
    return ev ? ev.color : 'bg-slate-100 text-slate-700';
  };

  const getEventLabel = (type) => {
    const ev = EVENT_TYPES.find(e => e.value === type);
    return ev ? ev.label : type;
  };

  const currentVars = variables[form.event_type] || variables['general'] || [];

  if (loading) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center h-64" data-testid="templates-loading">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="email-templates-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]" data-testid="templates-page-title">{t('et_title')}</h1>
            <p className="text-slate-500 mt-1">{t('et_subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="seed-defaults-btn"
              variant="outline"
              onClick={handleSeedDefaults}
              disabled={seedingDefaults}
              className="text-sm"
            >
              {seedingDefaults ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              {t('et_add_defaults')}
            </Button>
            <Button
              data-testid="create-template-btn"
              onClick={openCreateModal}
              className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white"
            >
              <Plus className="w-4 h-4 mr-1" /> {t('et_create')}
            </Button>
          </div>
        </div>

        {/* Template List */}
        {templates.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-300">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Mail className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium mb-1">{t('et_empty')}</p>
              <p className="text-slate-400 text-sm mb-4">{t('et_empty_hint')}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleSeedDefaults} disabled={seedingDefaults}>
                  {seedingDefaults ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  {t('et_add_defaults')}
                </Button>
                <Button onClick={openCreateModal} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white">
                  <Plus className="w-4 h-4 mr-1" /> {t('et_create')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {templates.map((tmpl, idx) => (
              <motion.div
                key={tmpl.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <Card
                  data-testid={`template-card-${tmpl.id}`}
                  className={`border transition-colors hover:border-slate-300 ${!tmpl.is_active ? 'opacity-60' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-semibold text-[#1e3a5f] truncate">{tmpl.name}</span>
                          <Badge variant="secondary" className={`text-xs ${getEventColor(tmpl.event_type)}`}>
                            {getEventLabel(tmpl.event_type)}
                          </Badge>
                          <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-600">
                            {tmpl.channel}
                          </Badge>
                          {!tmpl.is_active && (
                            <Badge variant="secondary" className="text-xs bg-red-50 text-red-600">
                              {t('et_inactive')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{tmpl.subject}</p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">{tmpl.body?.substring(0, 100)}...</p>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          data-testid={`preview-btn-${tmpl.id}`}
                          variant="ghost" size="sm"
                          onClick={() => handlePreview(tmpl.id)}
                          title={t('et_preview')}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`send-test-btn-${tmpl.id}`}
                          variant="ghost" size="sm"
                          onClick={() => handleSendTest(tmpl.id)}
                          disabled={sendingTest === tmpl.id}
                          title={t('et_send_test')}
                        >
                          {sendingTest === tmpl.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                        <Button
                          data-testid={`edit-btn-${tmpl.id}`}
                          variant="ghost" size="sm"
                          onClick={() => openEditModal(tmpl)}
                          title={t('et_edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-btn-${tmpl.id}`}
                          variant="ghost" size="sm"
                          onClick={() => handleDelete(tmpl.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title={t('et_delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">
                {editingId ? t('et_edit_title') : t('et_create_title')}
              </DialogTitle>
              <DialogDescription>
                {editingId ? t('et_edit_desc') : t('et_create_desc')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              {/* Name + Channel */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('et_name')}</Label>
                  <Input
                    data-testid="template-name-input"
                    className="mt-1"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder={t('et_name_placeholder')}
                  />
                </div>
                <div>
                  <Label>{t('et_channel')}</Label>
                  <Select value={form.channel} onValueChange={v => setForm(p => ({ ...p, channel: v }))}>
                    <SelectTrigger className="mt-1" data-testid="template-channel-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CHANNELS.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Event Type */}
              <div>
                <Label>{t('et_event_type')}</Label>
                <Select value={form.event_type} onValueChange={v => setForm(p => ({ ...p, event_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="template-event-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(e => (
                      <SelectItem key={e.value} value={e.value}>
                        {e.label} - {EVENT_DESCRIPTIONS[e.value]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div>
                <Label>{t('et_subject')}</Label>
                <Input
                  data-testid="template-subject-input"
                  className="mt-1"
                  value={form.subject}
                  onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                  placeholder={t('et_subject_placeholder')}
                />
              </div>

              {/* Body + Placeholders */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>{t('et_body')}</Label>
                  <div className="relative">
                    <Button
                      data-testid="placeholders-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowVars(!showVars)}
                      className="text-xs h-7 gap-1"
                    >
                      <Copy className="w-3 h-3" /> {t('et_placeholders')} <ChevronDown className="w-3 h-3" />
                    </Button>
                    <AnimatePresence>
                      {showVars && (
                        <motion.div
                          data-testid="placeholders-dropdown"
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
                        >
                          <div className="p-2 space-y-0.5">
                            {currentVars.map(v => (
                              <button
                                key={v}
                                data-testid={`var-${v}`}
                                onClick={() => insertVariable(v)}
                                className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 flex items-center justify-between group"
                              >
                                <code className="text-xs text-slate-600">{`{{${v}}}`}</code>
                                <Copy className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                <Textarea
                  ref={bodyRef}
                  data-testid="template-body-input"
                  rows={8}
                  value={form.body}
                  onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                  placeholder={t('et_body_placeholder')}
                  className="font-mono text-sm"
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3 pt-1">
                <Switch
                  data-testid="template-active-toggle"
                  checked={form.is_active}
                  onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))}
                />
                <Label className="text-sm text-slate-600">{t('et_active')}</Label>
              </div>

              {/* Save/Cancel */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEditor(false)}>{t('et_cancel')}</Button>
                <Button
                  data-testid="template-save-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {editingId ? t('et_update') : t('et_save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">{t('et_preview_title')}</DialogTitle>
              <DialogDescription>{t('et_preview_desc')}</DialogDescription>
            </DialogHeader>

            {previewData && (
              <div className="space-y-4 mt-2">
                {/* Toggle Plain/HTML */}
                <div className="flex gap-2">
                  <Button
                    data-testid="preview-plain-btn"
                    variant={previewMode === 'plain' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('plain')}
                    className={previewMode === 'plain' ? 'bg-[#1e3a5f] text-white' : ''}
                  >
                    <FileText className="w-4 h-4 mr-1" /> {t('et_preview_plain')}
                  </Button>
                  <Button
                    data-testid="preview-email-btn"
                    variant={previewMode === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('email')}
                    className={previewMode === 'email' ? 'bg-[#1e3a5f] text-white' : ''}
                  >
                    <Mail className="w-4 h-4 mr-1" /> {t('et_preview_email')}
                  </Button>
                </div>

                <div className="text-sm font-medium text-slate-600">
                  {t('et_subject')}: <span className="text-[#1e3a5f]">{previewData.subject}</span>
                </div>

                {previewMode === 'plain' ? (
                  <div
                    data-testid="preview-plain-content"
                    className="bg-slate-50 p-4 rounded-lg border border-slate-200 whitespace-pre-wrap font-mono text-sm text-slate-700"
                  >
                    {previewData.body_plain}
                  </div>
                ) : (
                  <div
                    data-testid="preview-email-content"
                    className="border border-slate-200 rounded-lg overflow-hidden"
                  >
                    <iframe
                      srcDoc={previewData.body_html}
                      title="Email Preview"
                      className="w-full h-[400px] border-0"
                      sandbox=""
                    />
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ShopLayout>
  );
}
